import {
  LABEL_TIPO_CAIXA_CAIXA,
  LABEL_TIPO_CAIXA_FARDO,
  LABEL_TIPO_CAIXA_GALÉIA,
  LABEL_TIPO_CAIXA_SACO,
  POR_PESO_FIXO,
  POR_PESO_VARIAVEL_SEM_UNIDADE,
  POR_UNIDADE,
  TIPO_IMPRESSAO_UN,
} from './constants';
import { formatMoeda, formatPeso, formatPreco } from './formats';
import { getPrecoUnitarioVerdadeiroCaixa2 } from './precoVenda';

/**
 * Formatação compartilhada dos cartazes A4 de oferta.
 *
 * A lógica abaixo era duplicada dentro de
 * `Resources/Produtos/DialogoCartazesProdutos/index.js`. Foi centralizada aqui para
 * que a tela "Cartazes de Oferta" (`Resources/CartazesOferta/`) reaproveite exatamente
 * a mesma transformação, sem copiar código.
 */

const MAPA_LABEL_TIPO_CAIXA = {
  [LABEL_TIPO_CAIXA_CAIXA]: 'Caixa',
  [LABEL_TIPO_CAIXA_FARDO]: 'Fardo',
  [LABEL_TIPO_CAIXA_GALÉIA]: 'Galéia',
  [LABEL_TIPO_CAIXA_SACO]: 'Saco',
};

/**
 * Quebra o campo `descricao_no_cartaz` (no máximo duas linhas separadas por `\n`)
 * nas duas linhas que o layout do cartaz espera.
 */
function separarLinhasDescricao(descricaoNoCartaz) {
  const linhas = (descricaoNoCartaz || '').split('\n');
  return {
    descricao: linhas[0] || '',
    descricao2: linhas[1] || '',
  };
}

/**
 * Divide um preço já calculado nas partes inteira e decimal, como o layout A4 espera
 * (ex.: 12,90 -> `{ precoParteInteira: '12', precoParteReal: '90' }`). O comprimento
 * de `precoParteInteira` é o que seleciona o modelo de página em `DialogoCartazesProdutos/Document.js`.
 */
function separarPartesPreco(preco) {
  const [parteInteira = '', parteReal = ''] = formatPreco(preco)
    .trim()
    .split(',');
  return {
    precoParteInteira: parteInteira.replace('R$:', '').trim(),
    precoParteReal: parteReal.trim(),
  };
}

/**
 * Monta a linha de caixaria/atacado exibida abaixo do preço.
 * `textoQuantidade` já vem pronto ("com 12" ou "de 5,000 Kg").
 */
function montarLabelCaixa(tipoLabelCaixa, textoQuantidade, valorTotal) {
  const rotulo = MAPA_LABEL_TIPO_CAIXA[tipoLabelCaixa] || '';
  return `${rotulo} ${textoQuantidade} por ${formatMoeda(valorTotal)}`;
}

/**
 * Formata um produto (já carregado com `unidade` e `ofertasProdutos`) na estrutura
 * consumida pelos modelos A4 em `Resources/Produtos/DialogoCartazesProdutos/Pages/`.
 * O preço de oferta verdadeiro (menor entre `preco_venda` e ofertas ativas) é resolvido
 * por `getPrecoUnitarioVerdadeiroCaixa2`.
 *
 * @param {object} produto produto com `unidade`, `ofertasProdutos`, `descricao_no_cartaz`,
 *   `peso_medio`, `tipo_impressao`, `unidades_caixa_nivel_1`, `peso_caixa_nivel_1`,
 *   `referencia_no_cartaz`, `tipo_label_caixa` e `copias`.
 */
export function formatarDadosCartaz(produto) {
  const {
    descricao_no_cartaz: descricaoNoCartaz,
    peso_medio: pesoMedio,
    tipo_impressao: tipoImpressao,
    unidades_caixa_nivel_1: unidadesCaixaNivel1,
    peso_caixa_nivel_1: pesoCaixaNivel1,
    referencia_no_cartaz: referenciaNoCartaz,
    tipo_label_caixa: tipoLabelCaixa,
    copias,
  } = produto;

  const tipoUnidade = produto.unidade.tipo;
  const preco = getPrecoUnitarioVerdadeiroCaixa2(produto);

  const dados = {
    copias,
    ...separarLinhasDescricao(descricaoNoCartaz),
    ...separarPartesPreco(preco),
    unidade: '',
    mensagemPesoPadrao: '',
    labelCaixa: '',
  };

  switch (tipoUnidade) {
    case POR_PESO_FIXO: {
      const impressaoPorUnidade = tipoImpressao === TIPO_IMPRESSAO_UN;
      Object.assign(
        dados,
        separarPartesPreco(impressaoPorUnidade ? preco * pesoMedio : preco),
      );
      dados.unidade = impressaoPorUnidade ? 'Un' : 'Kg';
      dados.mensagemPesoPadrao = impressaoPorUnidade
        ? `Peso Padrão de ${formatPeso(pesoMedio)}`
        : '';
      dados.labelCaixa = unidadesCaixaNivel1
        ? montarLabelCaixa(
            tipoLabelCaixa,
            `com ${unidadesCaixaNivel1}`,
            unidadesCaixaNivel1 * preco * pesoMedio,
          )
        : '';
      break;
    }
    case POR_UNIDADE: {
      Object.assign(dados, separarPartesPreco(preco));
      dados.unidade = 'Un';
      dados.mensagemPesoPadrao = referenciaNoCartaz || '';
      dados.labelCaixa = unidadesCaixaNivel1
        ? montarLabelCaixa(
            tipoLabelCaixa,
            `com ${unidadesCaixaNivel1}`,
            unidadesCaixaNivel1 * preco,
          )
        : '';
      break;
    }
    case POR_PESO_VARIAVEL_SEM_UNIDADE: {
      Object.assign(dados, separarPartesPreco(preco));
      dados.unidade = 'Kg';
      dados.mensagemPesoPadrao = '';
      dados.labelCaixa = pesoCaixaNivel1
        ? montarLabelCaixa(
            tipoLabelCaixa,
            `de ${formatPeso(pesoCaixaNivel1)}`,
            pesoCaixaNivel1 * preco,
          )
        : '';
      break;
    }
    default:
      break;
  }

  return dados;
}

/**
 * Mensagem de erro (em PT, pronta para exibir ao usuário) quando um produto não tem
 * a descrição de cartaz cadastrada.
 */
export function mensagemProdutoSemDescricaoCartaz(produto) {
  return `O produto - ${produto.nome} - ID ${produto.id}, não possui a descrição de cartaz registrada!`;
}

/**
 * Formata uma lista de produtos para o gerador de cartazes A4.
 * Lança no primeiro produto sem `descricao_no_cartaz` — o chamador decide se
 * pré-valida a lista inteira antes (ver `Resources/CartazesOferta/ContainerDialogoGerarCartazes.js`)
 * ou apenas captura o erro (ver `DialogoCartazesProdutos/index.js`).
 */
export function montarItensCartaz(produtos) {
  return produtos.map((produto) => {
    if (!produto.descricao_no_cartaz) {
      // eslint-disable-next-line no-throw-literal
      throw mensagemProdutoSemDescricaoCartaz(produto);
    }
    return formatarDadosCartaz(produto);
  });
}
